import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import ReviewLoading from './ReviewLoading';
import ReportCompleted from './ReportCompleted';
import ReportIncomplete from './ReportIncomplete';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import {
  IncompleteSections,
  ReviewProps,
  checkReportIsReady,
  checkReportIsReadyToFinalize,
} from './utils';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import ReportCustomQuestions, { hasSavedAnswer } from './ReportCustomQuestions';
import { getCustomQuestionsByReportId, ReportCustomQuestionsAPI } from './api';
import { useClient } from 'shared/apollo';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import {
  AllReportStage,
  CustomQuestion,
} from 'admin/pages/ManageAccounts/CustomQuestions/utilts';
import { mapSurveyAPIResponse } from 'admin/pages/ManageAccounts/CustomQuestions/api';
import { ReportStage } from 'donor/portal/pages/FeedbackDashboard/api';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import { isReportInterim } from '../ReportFinalize/utils';

const matchSurveyStage = (
  surveyStage: AllReportStage[],
  stage: ReportStage,
) => {
  for (const s of surveyStage) {
    if (s === 'All Stages' || s === stage) return true;
  }
  return false;
};

const filterOutStage = (surveys: CustomQuestion[], stage: ReportStage) =>
  surveys.filter(s => matchSurveyStage(s.reportStage, stage));

const filterOutGuidanceText = (surveys: CustomQuestion[]) => {
  const result: CustomQuestion[] = [];
  surveys.forEach(s => {
    const newSurvey = { ...s };
    newSurvey.questions = s.questions.filter(
      q => q.questionType !== 'guidance',
    );
    result.push(newSurvey);
  });
  return result;
};

const StyledDiv = styled.div`
  margin-top: 48px;
  .header-section {
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
    .header-section-body {
      margin-top: ${SPACERS.medium};
      margin-bottom: ${SPACERS.medium};
    }
  }
`;

const Review: React.FC<ReviewProps> = ({
  report,
  reportId,
  answers,
  isReportPassed,
  programName,
  feedbackType,
  isInterimStage,
  isFinalizeStage,
  handlePress,
  reloadReport,
  reloadAnswers,
}) => {
  const client = useClient();
  const { impactSummary, impactDetails } = useIndicator();
  const [showReportSurvey, setShowReportSurvey] = useState<boolean>(false);
  const [customQuestions, setCustomQuestions] = useState<CustomQuestion[]>([]);
  const [requestState, makeRequest] = useGraphQLRequest<
    ReportCustomQuestionsAPI
  >();
  const [state, setState] = useState<
    'loading' | 'complete' | IncompleteSections[]
  >('loading');

  const handleShowSurvey = useCallback(() => {
    setShowReportSurvey(true);
  }, [setShowReportSurvey]);

  const isInterim = isReportInterim(report.stage);
  const isFinalizeLike = isFinalizeStage || isInterim;

  const handleHideSurvey = useCallback(() => {
    setShowReportSurvey(false);
  }, [setShowReportSurvey]);

  useEffect(() => {
    switch (requestState.type) {
      case 'REQUEST_SUCCESS': {
        const surveysWithGuidance = mapSurveyAPIResponse(
          requestState.data.allReportSurvey,
        );
        const surveys = filterOutGuidanceText(surveysWithGuidance);
        const surveysByStage = filterOutStage(
          surveys,
          isFinalizeStage ? 'FINAL' : isInterim ? 'INTERIM' : 'INITIAL',
        );
        setCustomQuestions(surveysByStage);
        const hasUnanswered = surveysByStage.some(s =>
          s.questions.some(q => !hasSavedAnswer(q)),
        );
        if (hasUnanswered) {
          setShowReportSurvey(true);
        }
        break;
      }
      case 'REQUEST_ERROR':
        setCustomQuestions([]);
        break;

      default:
        break;
    }
  }, [requestState, setCustomQuestions, report.visibility]);

  useEffect(() => {
    makeRequest(getCustomQuestionsByReportId(client!, reportId));
  }, []);

  useEffect(() => {
    // To mimic the loading for better UX
    window.setTimeout(() => {
      const incompleteSections = isFinalizeLike
        ? checkReportIsReadyToFinalize(answers, report.stage).list
        : checkReportIsReady(
            answers,
            impactSummary,
            impactDetails,
            isReportPassed,
          ).list;
      setState(
        incompleteSections.length === 0 ? 'complete' : incompleteSections,
      );
    }, 2000);
  }, [answers, setState, isFinalizeLike, report.stage]);

  const isLoading = state === 'loading';
  const isCompleted = state === 'complete';
  const isInProgress = typeof state === 'object';

  if (
    requestState.type === 'REQUEST_INIT' ||
    requestState.type === 'REQUEST_START'
  ) {
    return <TrueImpactLoading />;
  }

  if (requestState.type === 'REQUEST_ERROR') {
    return <StyledDiv>Something went wrong</StyledDiv>;
  }

  if (showReportSurvey && isCompleted) {
    return (
      <ReportCustomQuestions
        reportId={reportId}
        programName={programName}
        feedbackType={feedbackType}
        isReportPassed={isReportPassed}
        isFinalizeStage={isFinalizeStage}
        customQuestions={customQuestions}
        handleHideSurvey={handleHideSurvey}
        setCustomQuestions={setCustomQuestions}
      />
    );
  }

  return (
    <StyledDiv>
      <div className="header-section">
        <TrueImpactText variant="heading-page">Almost done!</TrueImpactText>
        <TrueImpactText className="header-section-body">
          All that’s left is a quick review of your report to make sure it’s as
          accurate as can be. We’ll let you know if anything urgent needs your
          attention.
        </TrueImpactText>
      </div>
      {isLoading && <ReviewLoading />}
      {isCompleted && (
        <ReportCompleted
          report={report}
          answers={answers}
          reportId={reportId}
          programName={programName}
          feedbackType={feedbackType}
          reloadAnswers={reloadAnswers}
          reloadReport={reloadReport}
          isFinalizeStage={isFinalizeStage}
          handleShowSurvey={
            customQuestions.length === 0 ? undefined : handleShowSurvey
          }
        />
      )}
      {isInProgress && (
        <ReportIncomplete
          sectionId={isInterim ? 1 : isFinalizeStage ? 2 : 0}
          incompleteSections={state}
          handlePress={handlePress}
        />
      )}
    </StyledDiv>
  );
};

export default Review;
